export type DiagStatus = "ok" | "warn" | "err";

export type DiagCheck = {
  key: string;
  status: DiagStatus;
  label: string;
  sub: string;
  fix?: string;
};

export type DiagProfile = {
  orgId: string;
};

export type DiagUser = {
  emailVerified: boolean;
  invitePending: boolean;
  /**
   * Account is used only via a linked parent account (Schüler*innen ohne
   * eigenen Login). Such users intentionally have no own e-mail, so the
   * e-mail/invite checks don't apply — they are not a "problem".
   */
  impersonateOnly?: boolean;
  email?: string;
  /**
   * The password hash itself is `@private`, so this timestamp is the only
   * evidence of a local password the frontend can see.
   */
  passwordChangedAt?: string | null;
  twoFactor: string; // "active" | "none"
  federation?: { provider: string; fedId?: string | null } | null;
  profiles: DiagProfile[];
};

export type DiagResult = {
  checks: DiagCheck[];
  worst: DiagStatus;
};

/**
 * Derived login-diagnosis (ported from the prototype data.jsx `diagnose()`).
 * Pure — computes why a user can / cannot sign in from existing fields only.
 *
 * Deliberately absent: account lock, 2FA lockout and an "SSO-only org" check.
 * None has a readable field (`User` has no status flag, `TwoFactorMethod` no
 * failed-attempt count, `Organization.authentication` is @private), so a check
 * could only ever report "fine" and would promise a fix that does not exist.
 * Add them back together with the fields. Product access is not a login
 * question either — it has its own card.
 */
export function diagnose(user: DiagUser): DiagResult {
  const checks: DiagCheck[] = [];

  if (user.impersonateOnly) {
    // No own login: the account is used only via a linked parent account, so
    // it intentionally has no own e-mail. The e-mail/invite checks below don't
    // apply — this is not a "problem".
    checks.push({
      key: "email",
      status: "ok",
      label: "Kein eigener Login",
      sub: "Zugang nur über den verknüpften Elternaccount — eigene E-Mail nicht erforderlich.",
    });
  } else {
    checks.push(
      !user.email
        ? {
            // Nothing to confirm and nothing to mark verified: the account has
            // no login address at all.
            key: "email",
            status: "err",
            label: "Keine E-Mail-Adresse",
            sub: "Ohne Login-Adresse ist keine Anmeldung möglich. Adresse über „Bearbeiten“ ergänzen.",
          }
        : user.emailVerified
          ? { key: "email", status: "ok", label: "E-Mail bestätigt", sub: user.email }
          : {
              key: "email",
              status: "err",
              label: "E-Mail nicht bestätigt",
              sub: "Bestätigungslink wurde noch nicht geöffnet.",
              // The auth-server has no admin endpoint for re-sending a plain
              // confirmation mail — marking it verified is what support can do.
              fix: "E-Mail als bestätigt markieren",
            },
    );

    checks.push(
      user.invitePending && !user.email
        ? {
            // An invitation only exists as an e-mail. Without an address it can
            // never have reached anyone — the account is stuck, not "pending".
            // No fix: nothing can be sent until an address is set (Bearbeiten).
            key: "invite",
            status: "err",
            label: "Einladung ohne E-Mail-Adresse",
            sub: "Die Einladung kann nie zugestellt worden sein. E-Mail-Adresse über „Bearbeiten“ ergänzen und die Einladung erneut senden.",
          }
        : user.invitePending
          ? {
              key: "invite",
              status: "warn",
              label: "Einladung offen",
              sub: "Erstanmeldung noch nicht abgeschlossen.",
              fix: "Einladung erneut senden",
            }
          : {
              key: "invite",
              status: "ok",
              label: "Erstzugang abgeschlossen",
              sub: "Konto wurde aktiviert.",
            },
    );
  }

  checks.push(passwordCheck(user));

  if (user.twoFactor === "active") {
    checks.push({
      key: "2fa",
      status: "ok",
      label: "2FA aktiv",
      sub: "Authenticator-App registriert.",
    });
  } else {
    // No policy data exists (nothing enforces 2FA per role), so say no more.
    checks.push({
      key: "2fa",
      status: "ok",
      label: "2FA nicht eingerichtet",
      sub: "Optional.",
    });
  }

  if (user.federation) {
    checks.push({
      key: "fed",
      status: "ok",
      label: "SSO-Anbindung aktiv",
      sub: `Verknüpft mit ${user.federation.provider}.`,
    });
  }

  const worst: DiagStatus = checks.some((c) => c.status === "err")
    ? "err"
    : checks.some((c) => c.status === "warn")
      ? "warn"
      : "ok";

  return { checks, worst };
}

/**
 * Whether a local password exists cannot be read (the hash is `@private`); the
 * change timestamp is all there is. It is written by every path that sets a
 * password (auth-server signup/reset and the support action alike), so "no
 * timestamp" on an invited account means "no password yet", while on an
 * active one the password simply predates the timestamp.
 */
function passwordCheck(user: DiagUser): DiagCheck {
  if (user.passwordChangedAt) {
    const at = new Date(user.passwordChangedAt).toLocaleString("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
    return { key: "pw", status: "ok", label: "Passwort zuletzt geändert", sub: `Am ${at}.` };
  }
  if (user.invitePending) {
    return {
      key: "pw",
      status: "ok",
      label: "Noch kein Passwort",
      sub: "Wird bei der Erstanmeldung über den Einladungslink vergeben.",
    };
  }
  return {
    key: "pw",
    status: "ok",
    label: "Passwort-Änderung unbekannt",
    sub: "Kein Änderungsdatum hinterlegt — das Passwort stammt aus der Zeit vor dem Zeitstempel.",
  };
}

/** The account fields the diagnosis reads, as the person page loads them. */
export type DiagnosableAccount = {
  uuid: string;
  email?: string | null;
  emailVerified?: boolean | null;
  impersonateOnly?: boolean | null;
  invitationOtpValidUntil?: string | null;
  passwordChangedAt?: string | null;
  twoFactorMethods?: ReadonlyArray<unknown> | null;
  federatedClient?: ReadonlyArray<{ name?: string | null; label?: string | null }> | null;
};

/** Pure diagnosis input for an account. */
export function diagUserFromAccount(user: DiagnosableAccount): DiagUser {
  // 2FA from TOTP methods, federation from the linked federated client.
  const fed = user.federatedClient?.[0];
  return {
    emailVerified: Boolean(user.emailVerified),
    invitePending: Boolean(user.invitationOtpValidUntil),
    impersonateOnly: Boolean(user.impersonateOnly),
    email: user.email ?? undefined,
    passwordChangedAt: user.passwordChangedAt,
    twoFactor: (user.twoFactorMethods?.length ?? 0) > 0 ? "active" : "none",
    federation: fed ? { provider: fed.label || fed.name || "SSO" } : null,
    profiles: [],
  };
}

/** Banner copy per overall result. */
export const DIAG_SUMMARY: Record<DiagStatus, { title: string; sub: string }> = {
  ok: {
    title: "Keine Auffälligkeiten",
    sub: "Anmeldung sollte funktionieren — alle Prüfungen bestanden.",
  },
  warn: {
    title: "Einschränkungen gefunden",
    sub: "Der Login funktioniert, aber es gibt behebbare Punkte.",
  },
  err: {
    title: "Anmeldung blockiert",
    sub: "Mindestens eine Prüfung verhindert den Login.",
  },
};
